import React, { useState, useEffect, useRef } from "react";

// Synchronized precisely to match our production-grade 10-part country guide structure
const SECTIONS_CONFIG = [
  { id: "landscape", label: "📊 1. Fundraising Landscape" },
  { id: "instruments", label: "⚙ 2. Funding Instruments" },
  { id: "allocators", label: "💼 3. Sourcing Alternative Assets" },
  { id: "roadmap", label: "✅ 4. Sourcing & Founder Roadmap" },
  { id: "conclusion", label: "🏁 5. Final Thoughts" },
  { id: "faqs", label: "💡 Advanced Knowledge Base" },
];

const TableOfContents = () => {
  const [activeSection, setActiveSection] = useState("");
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  // Modern Intersection Observer instead of high-overhead scroll listeners
  useEffect(() => {
    const observerOptions = {
      root: null, // Relative to the browser viewport bounds
      rootMargin: "-20% 0px -55% 0px", // Calibrated intersection window box
      threshold: 0,
    };

    const handleIntersection = (entries) => {
      // Guard: Block dynamic intersection state updates if a smooth scroll trace is in progress
      if (isScrollingRef.current) return;

      for (const entry of entries) {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
          break; // Snaps to the upper intersecting section target instantly
        }
      }
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);

    // Dynamic node discovery loop matches element presence to safety arrays
    SECTIONS_CONFIG.forEach((sec) => {
      const element = document.getElementById(sec.id);
      if (element) observer.observe(element);
    });

    return () => {
      observer.disconnect();
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  // Performance-optimized programmatic smooth scroller
  const handleScrollTo = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (!element) return;

    // Mutate state locks to prevent scroll spy flashing artifacts
    isScrollingRef.current = true;
    setActiveSection(id);

    const safeClearanceOffset = 90; // Precise clearance baseline gap for fixed menu layers
    const targetElementPosition = element.getBoundingClientRect().top + window.scrollY;
    
    window.scrollTo({
      top: targetElementPosition - safeClearanceOffset,
      behavior: "smooth",
    });

    // Release intersection tracking locks safely after motion settle windows finish
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
    }, 850);
  };

  return (
    <nav className="w-full lg:w-64 shrink-0 lg:sticky lg:top-24 h-fit bg-gray-50 border border-gray-200/80 p-5 rounded-2xl shadow-sm bg-white/70 backdrop-blur-md">
      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
        Guide Navigation
      </h4>
      <ul className="space-y-1">
        {SECTIONS_CONFIG.map((sec) => {
          const isActive = activeSection === sec.id;
          return (
            <li key={sec.id}>
              <a
                href={`#${sec.id}`}
                onClick={(e) => handleScrollTo(e, sec.id)}
                className={`block text-sm font-semibold py-2.5 px-3 rounded-xl transition-all duration-150 transform active:scale-95 ${
                  isActive
                    ? "bg-blue-600 text-white shadow-md shadow-blue-500/20 translate-x-1"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100/80"
                }`}
              >
                {sec.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default TableOfContents;
